import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import MockMap from "@/components/MockMap";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import { regionByCode } from "@/data/regions";
import { ArrowLeft, ArrowRight, Path, Clock, CurrencyEur, Airplane, CheckCircle } from "@phosphor-icons/react";

const CANTONS = { ZH: "Zürich", BE: "Bern", LU: "Luzern", UR: "Uri", SZ: "Schwyz", OW: "Obwalden", NW: "Nidwalden", GL: "Glarus", ZG: "Zug", FR: "Fribourg", SO: "Solothurn", BS: "Basel-Stadt", BL: "Basel-Landschaft", SH: "Schaffhausen", AR: "Appenzell A.Rh.", AI: "Appenzell I.Rh.", SG: "St. Gallen", GR: "Graubünden", AG: "Aargau", TG: "Thurgau", TI: "Tessin", VD: "Waadt", VS: "Wallis", NE: "Neuenburg", GE: "Genf", JU: "Jura", FL: "Liechtenstein" };

export default function DestinationDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [fare, setFare] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/cities").then((r) => {
      const c = r.data.find((x) => x.id === id);
      if (!c) { navigate("/destinations"); return; }
      setCity(c);
    });
    api.post("/fare", { pickup_id: "zrh-airport", dropoff_id: id, vehicle_class: "standard", time: "10:00" })
      .then((r) => setFare(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line

  if (!city) return null;
  const today = new Date().toISOString().slice(0, 10);
  const book = () => navigate("/book", { state: { pickup_id: "zrh-airport", pickup_name: "Flughafen Zürich (ZRH)", dropoff_id: city.id, dropoff_name: city.name, date: today, time: "10:00" } });
  const canton = CANTONS[city.canton] || city.canton;

  const intro = lang === "de"
    ? `Buchen Sie Ihren privaten Flughafentransfer zum Festpreis zwischen dem Flughafen Zürich (ZRH) und ${city.name} im Kanton ${canton}. Professionelle Chauffeure, Flugüberwachung und Verfügbarkeit rund um die Uhr — reisen Sie bequem und pünktlich nach ${city.name}.`
    : `Book your private, fixed-fare airport transfer between Zürich Airport (ZRH) and ${city.name} in the canton of ${canton}. Professional chauffeurs, flight tracking and 24/7 availability — travel in comfort and on time to ${city.name}.`;
  const para2 = lang === "de"
    ? `Ihr Chauffeur empfängt Sie in der Ankunftshalle und bringt Sie direkt zu Ihrer Adresse in ${city.name} — kein Umsteigen, kein Warten, kein Stress. Ideal für Geschäftsreisen, Ferien und Familien.`
    : `Your chauffeur greets you in the arrivals hall and takes you straight to your address in ${city.name} — no changes, no waiting, no stress. Perfect for business trips, holidays and families.`;

  const benefits = lang === "de"
    ? ["Festpreis, keine versteckten Kosten", "Meet & Greet in der Ankunftshalle", "Gratis Wartezeit bei Flugverspätung", "24/7 verfügbar, 365 Tage"]
    : ["Fixed fare, no hidden costs", "Meet & greet in arrivals", "Free wait time on flight delays", "Available 24/7, 365 days"];

  const region = regionByCode(city.canton);
  const faqs = lang === "de" ? [
    { q: `Wie komme ich vom Flughafen Zürich nach ${city.name}?`, a: `Mit unserem privaten Flughafentransfer ab dem Flughafen Zürich (ZRH) – Meet & Greet, Festpreis und direkte Fahrt ohne Umsteigen nach ${city.name}.` },
    { q: `Was kostet das Taxi von Zürich Flughafen nach ${city.name}?`, a: fare ? `Der Festpreis für einen Standard-Transfer nach ${city.name} beträgt ab CHF ${fare.total.toFixed(0)} – transparent und ohne versteckte Kosten.` : `Sie erhalten einen transparenten Festpreis, bevor Sie buchen – ohne versteckte Kosten.` },
    { q: `Ist der Transfer nach ${city.name} auch nachts verfügbar?`, a: `Ja, wir fahren 24/7, 365 Tage im Jahr und überwachen Ihren Flug bei Verspätungen.` },
  ] : [
    { q: `How do I get from Zürich Airport to ${city.name}?`, a: `With our private airport transfer from Zürich Airport (ZRH) – meet & greet, a fixed fare and a direct ride with no changes to ${city.name}.` },
    { q: `How much is a taxi from Zürich Airport to ${city.name}?`, a: fare ? `The fixed fare for a standard transfer to ${city.name} starts from CHF ${fare.total.toFixed(0)} – transparent and with no hidden costs.` : `You get a transparent fixed fare before you book – with no hidden costs.` },
    { q: `Is the transfer to ${city.name} available at night?`, a: `Yes, we operate 24/7, 365 days a year and monitor your flight in case of delays.` },
  ];
  const seoTitle = lang === "de"
    ? `Airport Transfer Zurich → ${city.name} | Festpreis-Taxi ab ZRH`
    : `Zürich Airport Transfer to ${city.name} | Fixed-Fare Taxi from ZRH`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="destination-detail-page">
      <Seo title={seoTitle} description={intro} path={`/destinations/${city.id}`}
        jsonLd={[orgSchema(),
          breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_destinations"), path: "/destinations" }, { name: city.name, path: `/destinations/${city.id}` }]),
          { "@context": "https://schema.org", "@type": "Service", serviceType: "Airport transfer", provider: { "@type": "TaxiService", name: "Airport Transfer Zurich" }, areaServed: { "@type": "City", name: city.name }, name: `Airport Transfer Zurich → ${city.name}` },
          faqSchema(faqs)]} />
      <Link to="/destinations" className="inline-flex items-center gap-2 font-body text-sm text-[#5A607A] hover:text-[#0B1533] transition-colors duration-200 mb-6"><ArrowLeft size={15} /> {t("dest_all")}</Link>

      <div className="flex items-center gap-2 mb-3">
        <span className="font-body text-xs font-semibold text-[#EF5A1E] bg-[#EEF1FF] rounded-full px-3 py-1">{canton}</span>
      </div>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight">Flughafentaxi Zürich → {city.name}</h1>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mt-8">
        {[
          { icon: Path, label: t("dest_distance"), val: fare ? `${fare.distance_km} km` : "…" },
          { icon: Clock, label: t("dest_travel_time"), val: fare ? `~${fare.eta_minutes} min` : "…" },
          { icon: CurrencyEur, label: t("dest_price_from"), val: fare ? `CHF ${fare.total.toFixed(0)}` : "…" },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`dest-stat-${i}`}>
            <s.icon size={20} weight="regular" className="text-[#EF5A1E]" />
            <p className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mt-3">{s.val}</p>
            <p className="font-body text-[10px] text-[#5A607A] uppercase tracking-wider mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-3xl overflow-hidden border border-[#E9E5DE] h-64 mt-8">
        <MockMap progress={0} label={city.name} showCar={false} />
      </div>

      <div className="mt-8 space-y-5">
        <p className="font-body text-lg text-[#3A3F55] leading-relaxed">{intro}</p>
        <p className="font-body text-lg text-[#3A3F55] leading-relaxed">{para2}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-8">
        {benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl bg-[#F5F2ED] px-5 py-4">
            <CheckCircle size={20} weight="fill" className="text-[#EF5A1E] shrink-0" />
            <span className="font-body text-sm text-[#0B1533]">{b}</span>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-3xl bg-[#0B1533] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div className="flex items-center gap-3">
          <Airplane size={28} weight="fill" className="text-[#EF5A1E]" />
          <div>
            <p className="font-body text-xs text-white/50 uppercase tracking-wider">{t("dest_price_from")}</p>
            <p className="font-heading font-bold text-2xl text-white">{fare ? `CHF ${fare.total.toFixed(2)}` : "…"}</p>
          </div>
        </div>
        <button onClick={book} data-testid="dest-book-btn" className="inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-8 py-4 hover:bg-[#D2450F] transition-colors duration-200">
          {t("dest_book_now")} <ArrowRight size={16} />
        </button>
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("faq_title")}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`dest-faq-${i}`}>
              <p className="font-body font-semibold text-[#0B1533]">{f.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      {region && (
        <Link to={`/regionen/${region.slug}`} data-testid="dest-region-link" className="mt-8 inline-flex items-center gap-2 font-body text-sm font-semibold text-[#EF5A1E] hover:gap-3 transition-all">
          {lang === "de" ? `Alle Transfers im Kanton ${canton}` : `All transfers in the canton of ${canton}`} <ArrowRight size={15} />
        </Link>
      )}
    </div>
  );
}
